import moment from 'jalali-moment'
import { useState } from 'react'
import { UseMutateFunction, useQuery } from 'react-query'
import { AxiosResponse } from 'axios'
import { FiInfo } from 'react-icons/fi'
import DotsLoading from 'components/loadings/dotsLoading'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { getLoanCandidates, getMemberScore } from 'services/fund'
import { ScoreExplainer, ScoreMeter } from 'components/memberScore'
import { faPlain } from 'utilities/helper/format'
interface IPayload {
    id: string | undefined
    loanId: number
}
interface IItemProps {
    item: IDemandResult
    isLoading: boolean
    mutate: UseMutateFunction<
        AxiosResponse<any, any>,
        unknown,
        IPayload,
        unknown
    >
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    setModal: React.Dispatch<React.SetStateAction<IModal>>
}
interface IModal {
    show: boolean
    loanId: number
}
export default function Detail({
    item,
    isLoading,
    mutate,
    setIsOpen,
    setModal,
}: IItemProps) {
    const navigate = useNavigate()
    const { state } = useLocation()
    const { id: moneypoolId } = useParams()
    const [explaining, setExplaining] = useState(false)
    const poolshipId = item?.poolship?.id

    /**
     * رتبهٔ اعتباری — the ranking the اعطای وام wizard sorts by.
     *
     * This row used to be «اعتبار همیانی», `Member.global_credit`: a
     * sigmoid over `global_score`, a stored field left over from an older
     * product that nothing in the fund keeps up to date. It is the same
     * number for members who have behaved completely differently, it is not
     * about this fund at all, and a manager deciding on a loan was reading
     * it as if it meant something.
     *
     * امتیاز عضو is the number that does: تومان-روز a member has supplied
     * to THIS fund, minus what they borrowed, minus a penalty for late
     * instalments and plus a bonus for early ones. So the row now carries
     * the member's place in that ranking — the same rank, percentile and
     * (i) breakdown the grant wizard shows, from the same components, so a
     * change to the formula still lands in one place.
     *
     * Where it is read from follows the same rule as کارنامهٔ عضو: a
     * manager reads `loan-candidates`, which ranks the whole fund and is
     * shared (query key and all) with the wizard, so opening several
     * requests costs one scan. A plain member gets that list refused — it
     * carries every member's name, phone number and open request — so they
     * read `member-score`, their own row only, and only when the request is
     * theirs. `can_show_others_loan_demands` lets a fund show its members
     * each other's requests; their neighbours' ranking is not part of that,
     * so those rows simply have no score line.
     */
    const isManager = !!state?.isManager
    const scoreSource = state?.isManager
        ? 'candidates'
        : item?.is_poolship_requsted_loan
        ? 'own'
        : 'none'
    const candidates = useQuery(
        ['loan-candidates', moneypoolId, true, false],
        getLoanCandidates,
        {
            staleTime: 5 * 60 * 1000,
            enabled: !!moneypoolId && scoreSource === 'candidates',
        }
    )
    const ownScore = useQuery(
        ['member-score', moneypoolId, poolshipId],
        getMemberScore,
        {
            staleTime: 5 * 60 * 1000,
            enabled: !!moneypoolId && !!poolshipId && scoreSource === 'own',
        }
    )
    const scoreRow =
        scoreSource === 'candidates'
            ? candidates.data?.results?.find(
                  (row) => String(row.poolship_id) === String(poolshipId)
              )
            : ownScore.data?.result ?? undefined
    const scoreMeta =
        scoreSource === 'candidates' ? candidates.data : ownScore.data

    return (
        <>
            {!isLoading ? (
                <div className=" my-2 text-sm" onClick={() => setIsOpen(false)}>
                    <hr />
                    <div className=" flex justify-between">
                        <p className=" my-2">سرمایه شخصی</p>
                        <p className=" my-2">
                            {Number(item?.poolship?.capital).toLocaleString()}{' '}
                            تومان
                        </p>
                    </div>
                    <div className=" flex justify-between">
                        <p className=" my-2">بدهکاری</p>
                        <p className=" my-2">
                            {Number(
                                item?.poolship?.debit_amount
                            ).toLocaleString()}{' '}
                            تومان
                        </p>
                    </div>
                    <div className=" flex justify-between">
                        <p className=" my-2">شماره در صف انتظار</p>
                        <p className=" my-2">{item?.index}</p>
                    </div>
                    <div className=" flex justify-between">
                        <p className=" my-2">تعداد وام دریافتی</p>
                        <p className=" my-2">
                            {Number(item?.poolship?.num_loans).toLocaleString()}{' '}
                            وام
                        </p>
                    </div>
                    {scoreRow && (
                        <>
                            <div className=" flex justify-between">
                                <p className=" my-2">رتبهٔ اعتباری</p>
                                <div className=" my-2 flex items-center gap-1.5">
                                    <p>
                                        {faPlain.format(scoreRow.rank)} از{' '}
                                        {faPlain.format(
                                            scoreMeta?.members_count ?? 0
                                        )}{' '}
                                        عضو
                                    </p>
                                    {/* its own control, and it has to swallow
                                        the click: the row below is «close the
                                        card» and the card above it is «toggle
                                        the card» */}
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`توضیح رتبهٔ اعتباری ${scoreRow.member_name}`}
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            setExplaining(true)
                                        }}
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === 'Enter' ||
                                                event.key === ' '
                                            ) {
                                                event.preventDefault()
                                                event.stopPropagation()
                                                setExplaining(true)
                                            }
                                        }}
                                        className=" -m-1 shrink-0 cursor-pointer p-1 text-gray-6"
                                    >
                                        <FiInfo size={15} />
                                    </span>
                                </div>
                            </div>
                            {/* the rank alone says nothing about the shape of
                                the fund behind it — «۱۲ از ۵۷۹» and
                                «بالاتر از ۹۷٪» are the same fact only when
                                you do the division in your head */}
                            <div className=" mb-2">
                                <ScoreMeter
                                    percentile={scoreRow.percentile}
                                    size="sm"
                                />
                            </div>
                        </>
                    )}
                    <div className=" flex justify-between">
                        <p className=" my-2">تاریخ عضویت</p>
                        <p className=" my-2">
                            {moment
                                .from(
                                    item.poolship.jalali_join_date,
                                    'fa',
                                    'YYYY-MM-DD'
                                )
                                .locale('fa')
                                .format('YYYY')}
                            /
                            {moment
                                .from(
                                    item.poolship.jalali_join_date,
                                    'fa',
                                    'YYYY-MM-DD'
                                )
                                .locale('fa')
                                .format('MM')}
                            /
                            {moment
                                .from(
                                    item.poolship.jalali_join_date,
                                    'fa',
                                    'YYYY-MM-DD'
                                )
                                .locale('fa')
                                .format('DD')}
                        </p>
                    </div>
                    <div className=" flex justify-between">
                        <p className=" my-2">ضامن عضو</p>
                        <p className=" my-2">
                            {item?.poolship?.guarantor
                                ? String(
                                      item?.poolship?.guarantor.member
                                          .first_name
                                  ) +
                                  ' ' +
                                  String(
                                      item.poolship.guarantor.member.last_name
                                  )
                                : 'ثبت نشده'}
                        </p>
                    </div>
                    <div className=" flex justify-between">
                        <p className=" my-2">معرف عضو</p>
                        <p className=" my-2">
                            {item.poolship.recommender
                                ? String(
                                      item.poolship.recommender.member
                                          .first_name
                                  ) +
                                  ' ' +
                                  String(
                                      item.poolship.recommender.member.last_name
                                  )
                                : 'ثبت نشده'}
                        </p>
                    </div>
                    {/* Date AND time. The row above the fold shows the date,
                        but by the time a manager has this card open they are
                        reading a list of numbers about one person — and «کی
                        این را خواسته؟» is one of them. A year-old request is
                        not obvious from «۱۷:۵۶:۲۲». */}
                    <div className=" flex justify-between">
                        <p className=" my-2">زمان ثبت درخواست</p>
                        <p className=" my-2">
                            {moment
                                .from(item.created, 'YYYY-MM-DD')
                                .locale('fa')
                                .format('YYYY/MM/DD')}
                            {' — '}
                            {moment
                                .from(item.created, 'YYYY-MM-DD')
                                .locale('fa')
                                .format('HH:mm:ss')}
                        </p>
                    </div>
                    {/* Full width, not a label/value row: this is a paragraph
                        the member wrote, and the form told them «این پیام فقط
                        برای مدیران قابل مشاهده است» — so the one screen where
                        a manager reads their requests has to show it. */}
                    {!!item.user_description?.trim() && (
                        <div className=" my-2">
                            <p className=" my-2">توضیح عضو</p>
                            <p className=" whitespace-pre-line rounded-lg bg-gray-5 px-3 py-2 leading-7">
                                {item.user_description}
                            </p>
                        </div>
                    )}
                    {isManager ? (
                        <div className=" flex justify-between mx-2">
                            <button
                                className=" rounded-md  border-1 border-green-1 bg-green-2 ml-1 text-green-1 py-2 w-full"
                                onClick={(event) => {
                                    event.stopPropagation()
                                    navigate(
                                        `/loans/accept/management/add-new/${item?.poolship?.moneypool}/${item.poolship.id}`,
                                        {
                                            state: {
                                                name: state?.name,
                                                exist: state?.exist,
                                                amount: item.amount,
                                                // The loan goes to whoever the
                                                // request is FOR — `poolship` —
                                                // not to whoever filed it.
                                                // `registrar` is the same person
                                                // on a self-filed request, so
                                                // this only changes the
                                                // filed-on-behalf case, where it
                                                // used to send the wrong member:
                                                // the accept screen loads
                                                // `register`'s bank accounts and
                                                // submits `poolShip: register.id`,
                                                // so `selectedCardNumber` below
                                                // (the BORROWER's card) could
                                                // never match and the screen was
                                                // a dead end — «شماره حساب را
                                                // انتخاب کنید» with nothing to
                                                // pick that would satisfy it.
                                                register: item.poolship,
                                                num_repayment:
                                                    item.num_repayments,
                                                loan_demand: item.id,
                                                selectedCardNumber:
                                                    item.selected_card_number,
                                            },
                                        }
                                    )
                                }}
                            >
                                قبول
                            </button>
                            <button
                                className=" rounded-md bg-transparent border-1 mr-1 border-red-1 text-red-1 py-2 w-full"
                                onClick={(event) => {
                                    event.stopPropagation()
                                    setModal({
                                        show: true,
                                        loanId: item.id,
                                    })
                                }}
                            >
                                لغو
                            </button>
                        </div>
                    ) : (
                        <>
                            {item.is_poolship_requsted_loan && (
                                <button
                                    className=" rounded-md bg-transparent border-1 border-red-1 text-red-1 py-2 w-full"
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setModal({
                                            show: true,
                                            loanId: item.id,
                                        })
                                    }}
                                >
                                    لغو
                                </button>
                            )}
                        </>
                    )}
                </div>
            ) : (
                <DotsLoading />
            )}
            {/* A react-bootstrap Modal is a DOM portal, but React still
                bubbles its clicks up THIS tree — through «close the card»
                above and the card's own «toggle» above that. Without the
                wrapper, every tap inside the explainer collapses the
                request underneath it. */}
            {scoreRow && (
                <div onClick={(event) => event.stopPropagation()}>
                    <ScoreExplainer
                        show={explaining}
                        onHide={() => setExplaining(false)}
                        candidate={scoreRow}
                        overdueMultiplier={scoreMeta?.overdue_multiplier ?? 3}
                        earlyMultiplier={scoreMeta?.early_multiplier ?? 3}
                        membersCount={scoreMeta?.members_count ?? 0}
                        inflationApplied={false}
                    />
                </div>
            )}
        </>
    )
}
